import { create } from 'zustand';
import type { CustomNodeType } from '../services/customGraphApi';

/**
 * The AI assistant ↔ «Custom туршилт» (/relations/custom).
 *
 * - The graph publishes what is on its canvas (`canvas`) — the chat sends it with the
 *   question so the AI can name nodes («Айвико-г дэлгэ» → BID-…).
 * - The chat posts the AI's `graph_*` actions (`post`) — the graph runs them in order
 *   (`take`). Posted before the graph is open, they wait until it mounts.
 */
export interface GraphAction {
  type: string;
  payload?: Record<string, unknown>;
}

export interface CanvasSummary {
  nodes: Array<{ id: string; label: string; type: CustomNodeType }>;
  selected: string | null;
  total: number;
}

interface Batch {
  id: number;
  actions: GraphAction[];
  /** the rows of the SQL the AI ran — `graph_add` puts their entities on the canvas */
  rows: Record<string, unknown>[];
  /** who sent it, for the graph's notice — the AI assistant unless said */
  source?: string;
}

interface BusState {
  canvas: CanvasSummary | null;
  setCanvas: (c: CanvasSummary | null) => void;
  queue: Batch[];
  post: (actions: GraphAction[], rows: Record<string, unknown>[], source?: string) => void;
  take: () => Batch[];
}

export const useCustomGraphBus = create<BusState>()((set, get) => ({
  canvas: null,
  setCanvas: (canvas) => set({ canvas }),
  queue: [],
  post: (actions, rows, source) => set((s) => ({ queue: [...s.queue, { id: Date.now() + s.queue.length, actions, rows, source }] })),
  take: () => {
    const q = get().queue;
    if (q.length) set({ queue: [] });
    return q;
  },
}));

export const isGraphAction = (a: { type: string }) => a.type.startsWith('graph_');

/** The entities in a result row, as canvas nodes: the id columns the prompt asks for. */
const ID_COLUMNS: Array<[RegExp, string, CustomNodeType]> = [
  [/^supplier_id$/i, 'BID-', 'bidder'],
  [/^client_id$/i, 'ORG-', 'organization'],
  [/^tender_id$|^parent_tender_id$/i, 'TND-', 'tender'],
  [/^budget_governor_id$/i, 'GOV-', 'governor'],
  [/^person_id$/i, 'PER-', 'person'],
];

export function nodesFromRows(rows: Record<string, unknown>[]): Array<{ id: string; label: string; type: CustomNodeType }> {
  const out = new Map<string, { id: string; label: string; type: CustomNodeType }>();
  for (const row of rows) {
    const keys = Object.keys(row);
    const text = keys.filter((k) => typeof row[k] === 'string' && !/_id$/i.test(k));
    for (const [re, prefix, type] of ID_COLUMNS) {
      const col = keys.find((k) => re.test(k));
      const v = col ? row[col] : null;
      if (v == null || v === '') continue;
      const id = `${prefix}${String(v)}`;
      // the label: a name column of that entity, else the first text column
      const named = text.find((k) => (type === 'tender' ? /tender|тендер/i : type === 'organization' ? /org|захиалагч|байгууллага|client/i
        : type === 'person' ? /нэр|name|хүн|person/i : /компани|supplier|нийлүүлэгч|name|нэр/i).test(k));
      const label = String(row[named ?? text[0] ?? col!] ?? id);
      if (!out.has(id)) out.set(id, { id, label, type });
    }
  }
  return [...out.values()];
}
